import { gql } from '@apollo/client';
import {
  Box,
  Button,
  chakra,
  Flex,
  FormErrorMessage,
  Heading,
  HStack,
  Image,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { ErrorMessage } from '@hookform/error-message';
import { zodResolver } from '@hookform/resolvers/zod';
import { FunctionComponent, ReactNode, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useAuth } from '~auth/useAuth';
import { GridIcon, LoadingIcon, TrashIcon } from '~components/ui/icons';
import { Link } from '~components/ui/Link';
import { fromError } from '~utils/errors';
import {
  useInstallAppMutation,
  useRetryInstallAppMutation,
  useRetryUninstallAppMutation,
  useUninstallAppMutation,
} from './__generated__/useAppSubscriptions.graphql';
import { UseManageAppSubscriptionsForm_DisplayFragment } from './__generated__/useManageAppSubscriptionsForm.graphql';
import { InstalledAppSubscription, useAppSubscriptions } from './useAppSubscriptions';

export type AppSubscription =
  UseManageAppSubscriptionsForm_DisplayFragment['appSubscriptions'][number];

const schema = z.object({
  displayId: z.string(),
  appSubscriptionsToInstallIds: z.array(z.string()),
  appSubscriptionsToUninstallIds: z.array(z.string()),
  appSubscriptionsToRetryInstallIds: z.array(z.string()),
  appSubscriptionsToRetryUninstallIds: z.array(z.string()),
});

type FormValues = z.TypeOf<typeof schema>;

const getSubscriptionVersion = (appSubscription: AppSubscription) =>
  appSubscription.appInstallation?.__typename === 'AppInstallationInstalling' ||
  appSubscription.appInstallation?.__typename === 'AppInstallationInstalled' ||
  appSubscription.appInstallation?.__typename === 'AppInstallationUninstalling'
    ? appSubscription.appInstallation.versionName
    : null;

const SubHeading: FunctionComponent<{ children: ReactNode }> = ({ children }) => (
  <chakra.span
    color="gray.500"
    fontSize="xs"
    fontFamily="heading"
    fontWeight="bold"
    letterSpacing="0.15em"
    textTransform="uppercase"
  >
    {children}
  </chakra.span>
);

const AppEntry = ({
  subscription,
  onDelete,
  onInstall,
  progressLabel,
  unavailabilityReason,
  disabled = false,
}: {
  subscription: AppSubscription;
  onDelete?: () => void;
  onInstall?: () => void;
  progressLabel?: string;
  unavailabilityReason?: string;
  disabled?: boolean;
}) => (
  <HStack spacing={3} justifyContent="center" width="100%">
    <Box
      borderRadius="base"
      border="1px solid"
      borderColor="gray.100"
      overflow="hidden"
      opacity={disabled ? '0.5' : undefined}
    >
      {subscription.iconUrl ? (
        <Image
          width="48px"
          height="48px"
          src={subscription.iconUrl}
          alt={`App icon for ${subscription.name}`}
        />
      ) : (
        <Box display="flex" width="48px" height="48px" justifyContent="center" alignItems="center">
          <GridIcon color="gray.500" width={4} height={4} />
        </Box>
      )}
    </Box>
    <VStack alignItems="flex-start" spacing={0} flex={1}>
      <HStack spacing={3} alignItems="center">
        <Heading
          size="md"
          color="gray.900"
          mb="0"
          lineHeight="7"
          opacity={disabled ? '0.5' : undefined}
        >
          {subscription.name}
        </Heading>
        {progressLabel !== undefined && (
          <Tooltip label={progressLabel}>
            <Flex
              aria-label={progressLabel}
              justifyContent="center"
              alignItems="center"
              height="7"
              minWidth="7"
            >
              <LoadingIcon color="gray.400" height={5} width={5} />
            </Flex>
          </Tooltip>
        )}
      </HStack>
      <chakra.span color="gray.500" opacity={disabled ? '0.5' : undefined}>
        {getSubscriptionVersion(subscription) ?? <span>&nbsp;</span>}
      </chakra.span>
    </VStack>
    <Box>
      {onInstall !== undefined && (
        <Button
          size="sm"
          variant="outline"
          colorScheme="blue"
          onClick={onInstall}
          isDisabled={disabled}
        >
          Add
        </Button>
      )}
      {onDelete !== undefined && (
        <Button
          size="sm"
          variant="inline"
          colorScheme="gray"
          onClick={onDelete}
          isDisabled={disabled}
        >
          <TrashIcon color="gray.300" width="actionIconSize" height="actionIconSize" />
        </Button>
      )}
      {unavailabilityReason !== undefined && (
        <chakra.span color="gray.500">{unavailabilityReason}</chakra.span>
      )}
    </Box>
  </HStack>
);

export function useManageAppSubscriptionsForm({
  display,
  onSuccess,
  onCancel,
}: {
  display: UseManageAppSubscriptionsForm_DisplayFragment;
  onSuccess: () => Promise<void> | void;
  onCancel: () => void;
}) {
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      displayId: display.id,
      appSubscriptionsToInstallIds: [],
      appSubscriptionsToUninstallIds: [],
      appSubscriptionsToRetryInstallIds: [],
      appSubscriptionsToRetryUninstallIds: [],
    },
    resolver: zodResolver(schema),
  });
  const appSubscriptionsToUninstallIds = watch('appSubscriptionsToUninstallIds');
  const appSubscriptionsToInstallIds = watch('appSubscriptionsToInstallIds');
  const appSubscriptionsToRetryInstallIds = watch('appSubscriptionsToRetryInstallIds');
  const appSubscriptionsToRetryUninstallIds = watch('appSubscriptionsToRetryUninstallIds');

  // Get the subscriptions for this display
  const { getSortedAppSubscriptions } = useAppSubscriptions();
  const {
    installedAppSubscriptions: currentlyInstalledAppSubscriptions,
    availableAppSubscriptions: currentlyAvailableAppSubscriptions,
    unavailableAppSubscriptions,
    installFailedAppSubscriptions: currentlyInstallFailedAppSubscriptions,
    uninstallFailedAppSubscriptions: currentlyUninstallFailedAppSubscriptions,
  } = getSortedAppSubscriptions(display);

  const installFailedAppSubscriptionIds = currentlyInstallFailedAppSubscriptions.map(
    (subscription) => subscription.id,
  );
  const uninstallFailedAppSubscriptionIds = currentlyUninstallFailedAppSubscriptions.map(
    (subscription) => subscription.id,
  );
  const installedAppSubscriptionIds = currentlyInstalledAppSubscriptions.map(
    (subscription) => subscription.id,
  );
  const availableAppSubscriptionIds = currentlyAvailableAppSubscriptions.map(
    (subscription) => subscription.id,
  );

  const installAppSubscription = (subscriptionId: string) => {
    setValue(
      'appSubscriptionsToUninstallIds',
      appSubscriptionsToUninstallIds.filter((subId) => subId !== subscriptionId),
    );
    if (availableAppSubscriptionIds.includes(subscriptionId)) {
      setValue('appSubscriptionsToInstallIds', [...appSubscriptionsToInstallIds, subscriptionId]);
    }
    if (installFailedAppSubscriptionIds.includes(subscriptionId)) {
      setValue('appSubscriptionsToRetryInstallIds', [
        ...appSubscriptionsToRetryInstallIds,
        subscriptionId,
      ]);
    }
    if (uninstallFailedAppSubscriptionIds.includes(subscriptionId)) {
      setValue(
        'appSubscriptionsToRetryUninstallIds',
        appSubscriptionsToRetryUninstallIds.filter((subId) => subId !== subscriptionId),
      );
    }
  };

  const uninstallAppSubscription = (subscriptionId: string) => {
    setValue(
      'appSubscriptionsToInstallIds',
      appSubscriptionsToInstallIds.filter((subId) => subId !== subscriptionId),
    );
    if (installedAppSubscriptionIds.includes(subscriptionId)) {
      setValue('appSubscriptionsToUninstallIds', [
        ...appSubscriptionsToUninstallIds,
        subscriptionId,
      ]);
    }
    if (installFailedAppSubscriptionIds.includes(subscriptionId)) {
      setValue(
        'appSubscriptionsToRetryInstallIds',
        appSubscriptionsToRetryInstallIds.filter((subId) => subId !== subscriptionId),
      );
    }
    if (uninstallFailedAppSubscriptionIds.includes(subscriptionId)) {
      setValue('appSubscriptionsToRetryUninstallIds', [
        ...appSubscriptionsToRetryUninstallIds,
        subscriptionId,
      ]);
    }
  };

  // Construct the final arrays by applying the local changes onto the current data
  const installedAppSubscriptions: InstalledAppSubscription[] = [];
  const availableAppSubscriptions: AppSubscription[] = [];
  const installFailedAppSubscriptions: AppSubscription[] = [];
  const uninstallFailedAppSubscriptions: AppSubscription[] = [];

  for (const appSubscription of currentlyUninstallFailedAppSubscriptions) {
    if (appSubscriptionsToUninstallIds.includes(appSubscription.id)) {
      availableAppSubscriptions.push(appSubscription);
    } else if (appSubscriptionsToInstallIds.includes(appSubscription.id)) {
      installedAppSubscriptions.push({
        ...appSubscription,
        appInstallation: {
          __typename: 'AppInstallationInstalled',
          id: '',
          versionName: '',
        },
      });
    } else {
      uninstallFailedAppSubscriptions.push(appSubscription);
    }
  }
  for (const appSubscription of currentlyInstallFailedAppSubscriptions) {
    if (appSubscriptionsToUninstallIds.includes(appSubscription.id)) {
      availableAppSubscriptions.push(appSubscription);
    } else if (appSubscriptionsToInstallIds.includes(appSubscription.id)) {
      installedAppSubscriptions.push({
        ...appSubscription,
        appInstallation: {
          __typename: 'AppInstallationInstalled',
          id: '',
          versionName: '',
        },
      });
    } else {
      installFailedAppSubscriptions.push(appSubscription);
    }
  }
  for (const appSubscription of currentlyInstalledAppSubscriptions) {
    if (appSubscriptionsToUninstallIds.includes(appSubscription.id)) {
      availableAppSubscriptions.push(appSubscription);
    } else {
      installedAppSubscriptions.push(appSubscription);
    }
  }
  for (const appSubscription of currentlyAvailableAppSubscriptions) {
    if (appSubscriptionsToInstallIds.includes(appSubscription.id)) {
      installedAppSubscriptions.push({
        ...appSubscription,
        appInstallation: {
          __typename: 'AppInstallationInstalled',
          id: '',
          versionName: '',
        },
      });
    } else {
      // `unshift` so existing available apps are at the top of the list, above the to-uninstall apps from above
      availableAppSubscriptions.unshift(appSubscription);
    }
  }

  // Applying changes is only needed if local changes were made
  const canApplyChanges =
    appSubscriptionsToInstallIds.length > 0 || appSubscriptionsToUninstallIds.length > 0;

  const saveAppSubscriptions = useSaveAppSubscriptions({ display, onSuccess });
  const { organization } = useAuth();
  let organizationLink = '#';
  if (organization) {
    organizationLink = `/${organization.handle}/settings/apps`;
  }

  const body = (
    <VStack spacing="3" alignItems="flex-start">
      <ErrorMessage
        errors={errors}
        name="appSubscriptions"
        render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
      />

      {installFailedAppSubscriptions.length > 0 && (
        <>
          <SubHeading>Install Failed</SubHeading>
          {installFailedAppSubscriptions.map((appSubscription) => (
            <AppEntry
              key={appSubscription.id}
              subscription={appSubscription}
              onDelete={() => uninstallAppSubscription(appSubscription.id)}
            />
          ))}
        </>
      )}
      {uninstallFailedAppSubscriptions.length > 0 && (
        <>
          <SubHeading>Uninstall Failed</SubHeading>
          {uninstallFailedAppSubscriptions.map((appSubscription) => (
            <AppEntry
              key={appSubscription.id}
              subscription={appSubscription}
              onDelete={() => uninstallAppSubscription(appSubscription.id)}
            />
          ))}
        </>
      )}
      <SubHeading>Installed</SubHeading>
      {installedAppSubscriptions.length === 0 ? (
        <chakra.p>You haven’t added any apps to this display yet.</chakra.p>
      ) : (
        installedAppSubscriptions.map((appSubscription) => {
          const isDisabled =
            appSubscription.appInstallation.__typename !== 'AppInstallationInstalled';
          return (
            <AppEntry
              key={appSubscription.id}
              subscription={appSubscription}
              onDelete={() => uninstallAppSubscription(appSubscription.id)}
              disabled={isDisabled}
              progressLabel={appSubscription.progressLabel}
            />
          );
        })
      )}
      <SubHeading>Available</SubHeading>
      {availableAppSubscriptions.length === 0 ? (
        <chakra.p>
          You haven’t bought any apps yet. You can visit your{' '}
          <Link to={organizationLink} color="blue.500">
            organisation apps
          </Link>{' '}
          to start using Wave apps on your displays.
        </chakra.p>
      ) : (
        availableAppSubscriptions.map((appSubscription) => (
          <AppEntry
            key={appSubscription.id}
            subscription={appSubscription}
            onInstall={() => installAppSubscription(appSubscription.id)}
          />
        ))
      )}
      {unavailableAppSubscriptions.length > 0 && (
        <>
          <SubHeading>Unavailable</SubHeading>
          {unavailableAppSubscriptions.map((appSubscription) => (
            <AppEntry
              key={appSubscription.id}
              subscription={appSubscription}
              unavailabilityReason={appSubscription.reason}
            />
          ))}
        </>
      )}
    </VStack>
  );

  const footer = (
    <>
      <Button variant="ghost" colorScheme="blue" onClick={onCancel} isDisabled={isSubmitting}>
        Cancel
      </Button>
      <Button
        variant="solid"
        colorScheme="blue"
        marginLeft="3"
        type="submit"
        isDisabled={isSubmitting || !canApplyChanges}
        isLoading={isSubmitting}
      >
        Apply
      </Button>
    </>
  );

  return {
    onSubmit: handleSubmit(canApplyChanges ? saveAppSubscriptions : onSuccess),
    body,
    footer,
  };
}

export function useSaveAppSubscriptions({
  display,
  onSuccess,
}: {
  display: UseManageAppSubscriptionsForm_DisplayFragment;
  onSuccess?: () => Promise<void> | void;
}) {
  const toast = useToast();
  const [installAppMutation] = useInstallAppMutation();
  const [uninstallAppMutation] = useUninstallAppMutation();
  const [retryInstallAppMutation] = useRetryInstallAppMutation();
  const [retryUninstallAppMutation] = useRetryUninstallAppMutation();

  return useCallback(
    async ({
      displayId,
      appSubscriptionsToInstallIds,
      appSubscriptionsToUninstallIds,
      appSubscriptionsToRetryInstallIds,
      appSubscriptionsToRetryUninstallIds,
    }: FormValues) => {
      // Remove the subscriptions that failed to install. We have to retry them.
      const appSubscriptionsToInstallWithoutRetryIds = appSubscriptionsToInstallIds.filter(
        (subscriptionId) => !appSubscriptionsToRetryInstallIds.includes(subscriptionId),
      );
      for (const appSubscriptionId of appSubscriptionsToInstallWithoutRetryIds) {
        try {
          await installAppMutation({
            variables: {
              input: {
                displayId,
                subscriptionId: appSubscriptionId,
              },
            },
          });

          // TODO: add analytics here later through other ticket

          await onSuccess?.();
        } catch (error) {
          const appSubscription = display.appSubscriptions.find(
            ({ id }) => id === appSubscriptionId,
          );
          const formattedName =
            appSubscription !== undefined ? `"${appSubscription.name}"` : 'the app';

          toast({
            status: 'error',
            title: `Installing ${formattedName} failed. Please try again later.`,
            description: fromError(error, 'installAppMutation', {
              displayId,
              subscriptionId: appSubscriptionId,
            }),
          });
        }
      }
      // Remove the subscriptions that failed to install. We have to retry them.
      const appSubscriptionsToUninstallWithoutRetryIds = appSubscriptionsToUninstallIds.filter(
        (subscriptionId) => !appSubscriptionsToRetryUninstallIds.includes(subscriptionId),
      );
      for (const appSubscriptionId of appSubscriptionsToUninstallWithoutRetryIds) {
        try {
          await uninstallAppMutation({
            variables: {
              input: {
                displayId,
                subscriptionId: appSubscriptionId,
              },
            },
          });

          // TODO: add analytics here later through other ticket

          await onSuccess?.();
        } catch (error) {
          const appSubscription = display.appSubscriptions.find(
            ({ id }) => id === appSubscriptionId,
          );
          const formattedName =
            appSubscription !== undefined ? `"${appSubscription.name}"` : 'the app';

          toast({
            status: 'error',
            title: `Uninstalling ${formattedName} failed. Please try again later.`,
            description: fromError(error, 'uninstallAppMutation', {
              displayId,
              subscriptionId: appSubscriptionId,
            }),
          });
        }
      }
      for (const appSubscriptionId of appSubscriptionsToRetryInstallIds) {
        try {
          await retryInstallAppMutation({
            variables: {
              input: {
                displayId,
                subscriptionId: appSubscriptionId,
              },
            },
          });
          await onSuccess?.();
        } catch (error) {
          const appSubscription = display.appSubscriptions.find(
            ({ id }) => id === appSubscriptionId,
          );
          const formattedName =
            appSubscription !== undefined ? `"${appSubscription.name}"` : 'the app';

          toast({
            status: 'error',
            title: `Installing ${formattedName} failed. Please try again later.`,
            description: fromError(error, 'retryInstallAppMutation', {
              displayId,
              subscriptionId: appSubscriptionId,
            }),
          });
        }
      }
      for (const appSubscriptionId of appSubscriptionsToRetryUninstallIds) {
        try {
          await retryUninstallAppMutation({
            variables: {
              input: {
                displayId,
                subscriptionId: appSubscriptionId,
              },
            },
          });

          // TODO: add analytics here later through other ticket

          await onSuccess?.();
        } catch (error) {
          const appSubscription = display.appSubscriptions.find(
            ({ id }) => id === appSubscriptionId,
          );
          const formattedName =
            appSubscription !== undefined ? `"${appSubscription.name}"` : 'the app';

          toast({
            status: 'error',
            title: `Uninstalling ${formattedName} failed. Please try again later.`,
            description: fromError(error, 'retryUninstallAppMutation', {
              displayId,
              subscriptionId: appSubscriptionId,
            }),
          });
        }
      }
    },
    [
      installAppMutation,
      display.appSubscriptions,
      onSuccess,
      toast,
      uninstallAppMutation,
      retryInstallAppMutation,
      retryUninstallAppMutation,
    ],
  );
}

useManageAppSubscriptionsForm.graphql = {
  fragments: {
    DisplaySubscriptions: gql`
      fragment DisplaySubscriptions on DisplayAppSubscription {
        id
        name
        iconUrl
        appInstallation {
          id
          ... on AppInstallationInstalled {
            versionName
          }
          ... on AppInstallationInstalling {
            versionName
            downloadProgress
          }
          ... on AppInstallationUninstalling {
            versionName
          }
          ... on AppInstallationUpdating {
            currentVersionCode
            downloadProgress
          }
        }
        usage {
          current
          max
        }
      }
    `,
    useManageAppSubscriptionsForm_display: gql`
      fragment useManageAppSubscriptionsForm_display on Display {
        id
        appSubscriptions {
          ...DisplaySubscriptions
        }
      }
    `,
  },
};
